/**
 * CDN / S3 URL helpers for static assets.
 *
 * Two base URLs are available via environment variables:
 *
 *   VITE_CDN_URL – custom domain CDN (e.g. https://cdn.hooliganseyewear.com)
 *   VITE_S3_URL  – direct S3 bucket   (e.g. https://s3.us-east-1.amazonaws.com/cdn.hooliganseyewear)
 *
 * When a variable is empty or missing the original path is returned
 * unchanged, so local development works without any extra configuration.
 */

const CDN_BASE = (import.meta.env.VITE_CDN_URL || "").replace(/\/+$/, "");
const S3_BASE = (import.meta.env.VITE_S3_URL || "").replace(/\/+$/, "");

/* ── helpers ─────────────────────────────────────────────────────── */

/**
 * Normalise a path so it always starts with `/`.
 * @param {string} path
 */
function norm(path) {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Prefix a local asset path with the CDN base URL.
 *
 * @param {string} path – asset path (e.g. `/image/HOOLIGANS+WEB/About+Us/hero.jpg`)
 * @returns {string} full URL when CDN is configured, otherwise the original path
 */
export function cdnUrl(path) {
  if (!CDN_BASE || !path) return path;
  return `${CDN_BASE}${norm(path)}`;
}

/**
 * Prefix a local asset path with the S3 bucket URL.
 *
 * @param {string} path – asset path (e.g. `/image/catalog/Burberry/1909_BE2363_3874/APC_6362.jpg`)
 * @returns {string} full S3 URL, falls back to cdnUrl() if VITE_S3_URL is not set
 */
export function s3Url(path) {
  if (!path) return path;
  if (!S3_BASE) return cdnUrl(path);
  return `${S3_BASE}${norm(path)}`;
}

/**
 * Build a full URL for a product catalog image.
 *
 * Accepts either a full path or just the product-specific part:
 *   catalogImage("Burberry/1909_BE2363_3874/APC_6362.jpg")
 *   catalogImage("image/catalog/Burberry/1909_BE2363_3874/APC_6362.jpg")
 *
 * @param {string}  path           – image path within the catalog
 * @param {Object}  [opts]
 * @param {boolean} [opts.useS3]   – force the S3 direct URL (default: false, uses CDN)
 * @returns {string} full image URL
 */
export function catalogImage(path, { useS3 = false } = {}) {
  if (!path) return path;
  const full = path.startsWith("image/")
    ? `/${path}`
    : `/image/catalog/${path}`;
  return useS3 ? s3Url(full) : cdnUrl(full);
}

/* ── responsive rendition helpers (F2.6 — Phase 1, WIRED) ──────────────
 *
 * Real S3/CloudFront convention (verified June 2026 across Armani/Balenciaga/
 * Bottega and the 1445_BV1012S_003 folder listing):
 *   raster master : .../APC_6257.jpg              (kept)
 *   base avif      : .../APC_6257.avif            (the API returns THIS as
 *                                                  `image` when renditions exist)
 *   sized avif     : .../APC_6257-600x600.avif    (-{W}x{H} before the ext)
 *                    .../APC_6257-1200x1200.avif
 *   base webp      : .../APC_6257.webp            (NO sized webp exists yet)
 *
 * So sized renditions are SQUARE (1:1) and AVIF-only; webp is base-size only.
 * The backend already emits a `.avif` URL only for products whose renditions
 * exist, so `hasRenditions()` (URL ends in .avif) is a safe gate — products
 * without renditions keep their .png/.jpg master and get NO srcset.
 *
 * IMPORTANT: only emit widths that actually exist on S3 (600, 1200). Adding
 * others (300/400/800/…) would 404 until the backend generates them.
 */

/** Square AVIF widths that exist on the CDN today. */
export const RESPONSIVE_WIDTHS = [600, 1200];

/** Rendition aspect ratio — renditions are square (1:1) today. */
export function heightFor(width, ratio = 1) {
  return Math.round(width * ratio);
}

/** True when a URL points at a base AVIF (⇒ its sized renditions exist). */
export function hasRenditions(url) {
  return typeof url === "string" && /\.avif(\?|$)/i.test(url);
}

/**
 * Insert `-{w}x{h}` before the file extension of a rendition base URL.
 * renditionUrl(".../APC_6257.avif", 600) → ".../APC_6257-600x600.avif"
 * Returns "" for unsupported/extension-less URLs (caller should fall back).
 */
export function renditionUrl(baseUrl, width, { ratio = 1, format } = {}) {
  if (!baseUrl || !width) return "";
  const m = baseUrl.match(/^(.*)\.([a-z0-9]+)(\?.*)?$/i);
  if (!m) return "";
  const ext = format || m[2];
  return `${m[1]}-${width}x${heightFor(width, ratio)}.${ext}${m[3] || ""}`;
}

/**
 * Build a width-descriptor srcset string from a base rendition URL.
 * srcsetFor(".../APC_6257.avif", [600,1200], { format: "avif" })
 *   → ".../APC_6257-600x600.avif 600w, .../APC_6257-1200x1200.avif 1200w"
 */
export function srcsetFor(
  baseUrl,
  widths = RESPONSIVE_WIDTHS,
  { ratio = 1, format } = {}
) {
  if (!baseUrl) return "";
  return widths
    .map((w) => {
      const u = renditionUrl(baseUrl, w, { ratio, format });
      return u ? `${u} ${w}w` : "";
    })
    .filter(Boolean)
    .join(", ");
}

/**
 * AVIF srcset for a product image URL coming from the API. Returns "" when
 * the URL has no renditions (a .png/.jpg master) so callers fall back to a
 * plain <img>. Safe against 404s: only the 600 & 1200 square AVIFs are emitted.
 */
export function productAvifSrcset(apiImageUrl) {
  if (!hasRenditions(apiImageUrl)) return "";
  return srcsetFor(apiImageUrl, RESPONSIVE_WIDTHS, {
    ratio: 1,
    format: "avif",
  });
}

/**
 * Poster ("cover") image for a product video, derived from the clip's own URL.
 *
 * CONVENTION (must match the backend generator — see VIDEO-POSTERS.md):
 * the poster is the clip's basename with an `.avif` extension, living in the
 * same folder as the clip:
 *
 *   .../PROMO_1234.mp4  ->  .../PROMO_1234.avif
 *                           .../PROMO_1234-600x600.avif    (sized renditions)
 *                           .../PROMO_1234-1200x1200.avif
 *
 * Deliberately identical to the product-image convention, so a poster is just
 * "another product image" to the rest of the frontend: `hasRenditions()`,
 * `renditionUrl()` and `productAvifSrcset()` all work on it unchanged, and the
 * gallery/thumbnail code needs no video-specific branch.
 *
 * Returns "" for anything that is not a recognisable video URL. Callers must
 * still handle a 404 (the poster may not have been generated yet) by falling
 * back to the product's own photo — see `videoPosterFallback` in the gallery.
 *
 * @param {string} videoUrl
 * @returns {string} base poster URL, or "" when not derivable
 */
export function videoPosterUrl(videoUrl) {
  if (typeof videoUrl !== "string" || !videoUrl.trim()) return "";
  const m = videoUrl.trim().match(/^(.*)\.(mp4|webm|mov|m4v)(\?.*)?$/i);
  if (!m) return "";
  return `${m[1]}.avif${m[3] || ""}`;
}

/**
 * JPEG still of a managed product video, for browsers that reject the AVIF
 * poster.
 *
 * The Product Videos module encodes a clip with MediaConvert into
 * `.../{asset_uuid}/v1/clip.mp4` and builds the AVIF posters from a frame that
 * the same job captured as a JPEG beside it:
 *
 *   .../{asset_uuid}/v1/clip.mp4  ->  .../{asset_uuid}/v1/mediaconvert/original_frame.0000001.jpg
 *
 * (the module's source is always `original.{mp4|mov}`; see
 * `MediaConvert::expectedOutputKeys` in shared/opencart-tools/product-videos).
 * It is the same picture as the AVIF poster, so swapping to it changes nothing
 * visible. Returns "" for clips that are not module-managed (imported legacy
 * files have no such frame); callers fall back to the product photo.
 *
 * @param {string} videoUrl
 * @returns {string} frame URL, or "" when not derivable
 */
export function videoFrameUrl(videoUrl) {
  if (typeof videoUrl !== "string" || !videoUrl.trim()) return "";
  const m = videoUrl
    .trim()
    .match(/^(.*\/[0-9a-f]{32}\/v1\/)clip\.mp4(\?.*)?$/i);
  if (!m) return "";
  return `${m[1]}mediaconvert/original_frame.0000001.jpg${m[2] || ""}`;
}

/** Base WebP sibling for a product image URL (single size). "" when none. */
export function productWebpSrc(apiImageUrl) {
  if (!hasRenditions(apiImageUrl)) return "";
  return apiImageUrl.replace(/\.avif(\?|$)/i, ".webp$1");
}

/**
 * Consolidated responsive sources for a product image, derived from the API URL.
 * - Base .avif URL  -> AVIF srcset (600w + 1200w) + base WebP + avif <img> fallback.
 * - .jpg/.png/.webp -> hasRenditions:false; caller should render a plain <img src>.
 *
 * The raster <img> fallback is the URL itself (already AVIF for rendition
 * products, which is universally decodable; the raster master for the rest).
 * A true .jpg/.png <img> fallback needs the backend to return the raster URL,
 * because the original extension is not derivable from the AVIF name.
 *
 * @param {string} apiImageUrl
 * @param {Object} [opts]
 * @param {string} [opts.sizes] - the `sizes` attribute for the AVIF <source>.
 */
export function productPicture(apiImageUrl, { sizes = "100vw" } = {}) {
  const avifSrcset = productAvifSrcset(apiImageUrl); // "" when no renditions
  return {
    hasRenditions: !!avifSrcset,
    avifSrcset, //  "...-600x600.avif 600w, ...-1200x1200.avif 1200w"
    webpSrc: productWebpSrc(apiImageUrl), // "....webp" (base size) or ""
    imgSrc: apiImageUrl, // raster/base fallback
    sizes,
  };
}
